import Immutable from 'immutable';
import moment from 'moment';

const initialState = Immutable.Map({
  filter: 'booked',
  tourFilter: 'all',
  daySlots: {},
  previousView: null,
});

function setLoading(state) {
  return state.set('loading', true);
}

function unsetLoading(state) {
  return state.set('loading', false);
}

function indexedSlots(slots) {
  return slots.reduce((acc, slot) => {
    acc[slot.id] = slot;
    return acc;
  }, {});
}

function eventIntersectsMonth(event, month) {
  const eventRange = moment.range(
    moment(`${event.start.date} ${event.start.time}`, 'MM-DD-YYYY HH:mmA'),
    moment(`${event.end.date} ${event.end.time}`, 'MM-DD-YYYY HH:mmA')
  );

  const extendedMonth = moment.range(
    moment(month).startOf('month').startOf('isoWeek'),
    moment(month).endOf('month').endOf('isoWeek')
  );

  return eventRange.overlaps(extendedMonth);
}

function updateEvent(state, booking_slot) {
  const startDate = { ...booking_slot.date };
  const endDate = { ...booking_slot.end_date };
  // XXX moment counts months starting from zero
  startDate.month -= 1;
  endDate.month -= 1;

  const { event } = booking_slot;

  const searchRange = moment.range(
    moment(startDate).add(-1, 'month'),
    moment(endDate).add(1, 'month')
  );

  searchRange.by('month', (currentMonth) => {
    const year = currentMonth.get('year').toString();
    const month = (currentMonth.get('month') + 1).toString();
    const event_id = event.id.toString();

    if (eventIntersectsMonth(event, currentMonth)) {
      const existingEvent = state.getIn([year, month, event_id]);
      if (existingEvent && event.meta.style === 'empty') {
        state.deleteIn([year, month, event_id]);
      } else {
        state.setIn([year, month, event_id], Immutable.fromJS(event));
      }
    }
  });

  return state;
}

function bookingStateChanged(state, { booking_slot, previous_booking_slot }) {
  updateEvent(state, booking_slot);
  if (previous_booking_slot) {
    // If a reschedule happened we get a previous_booking_slot as well
    updateEvent(state, previous_booking_slot);
  }
  return state;
}

function addMonth(state, { date, slots, totals }) {
  const momentDate = moment(date, 'MM/DD/YYYY');
  const month = momentDate.format('M');
  const year = momentDate.format('YYYY');

  state.setIn([year, month], Immutable.fromJS(indexedSlots(slots)));
  state.setIn(['totals', year, month], Immutable.fromJS(totals));
  return unsetLoading(state);
}

// Used by daily manifest download popup
function addDay(state, { slots }) {
  state.setIn(['daySlots'], Immutable.fromJS(indexedSlots(slots)));
  return unsetLoading(state);
}

function setLoadingFailed(state) {
  state.set('error', true);
  return unsetLoading(state);
}

function toggleEventHover(state, { eventId, hoverState }) {
  return state.setIn(['event_hovered', eventId.toString()], hoverState);
}

function setFilter(state, { value }) {
  return state.merge({
    filter: value.slotType,
    tourFilter: value.tourIdOrType,
  });
}

function storePreviousView(state, previousView) {
  return state.set('previousView', previousView);
}

function clearPreviousView(state) {
  return state.set('previousView', null);
}

export default function reducer(state = initialState, { type, payload }) {
  return state.withMutations((mutableState) => {
    return (
      {
        CALENDAR_MONTH_LOAD_START: setLoading,
        CALENDAR_MONTH_LOAD_CACHED: unsetLoading,
        CALENDAR_MONTH_LOADED: addMonth,
        CALENDAR_MONTH_FAILED: setLoadingFailed,
        CALENDAR_DAY_LOAD_START: setLoading,
        CALENDAR_DAY_LOAD_CACHED: unsetLoading,
        CALENDAR_DAY_LOADED: addDay,
        CALENDAR_DAY_LOAD_FAILED: setLoadingFailed,
        CALENDAR_EVENT_HOVER_STATE: toggleEventHover,
        CALENDAR_SET_FILTER: setFilter,
        BOOKINGS_ACCEPT_COMPLETED: bookingStateChanged,
        BOOKINGS_REJECT_COMPLETED: bookingStateChanged,
        BOOKINGS_CANCEL_COMPLETED: bookingStateChanged,
        BOOKINGS_RESCHEDULE_COMPLETED: bookingStateChanged,
        STORE_PREVIOUS_CALENDAR_VIEW: storePreviousView,
        CLEAR_PREVIOUS_CALENDAR_VIEW: clearPreviousView,
      }[type] || (() => mutableState)
    )(mutableState, payload);
  });
}
